import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { authService } from '../services/authService';
import { ROUTES } from '../constants/routes';
import AuthPageLayout from '../components/layout/AuthPageLayout';
import MfaForm from '../components/auth/MfaForm';
import LoadingSpinner from '../components/common/LoadingSpinner';

const MfaPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mfaInitiatedRef = useRef(false);
  const { user, isAuthenticated, passedMfa, verifyMfa } = useAuth();
  const { isInitialized, showError, hideAlert } = useUI();
  const navigate = useNavigate();

  useEffect(() => {
    // If not authenticated at all, redirect to login
    if (isInitialized && !isAuthenticated) {
      navigate(ROUTES.LOGIN);
    }
    // If already passed MFA, redirect to React search page
    if (isInitialized && passedMfa) {
      navigate(ROUTES.SEARCH);
    }
  }, [isInitialized, isAuthenticated, passedMfa, navigate]);

  // Initiate MFA (send email) when page loads
  useEffect(() => {
    let isMounted = true;

    const sendMfaCode = async () => {
      if (isInitialized && isAuthenticated && !passedMfa && !mfaInitiatedRef.current) {
        mfaInitiatedRef.current = true;
        try {
          const response = await authService.initiateMfa();
          if (isMounted && !response.success) {
            showError('Failed to send verification code. Please try again.');
          }
        } catch {
          if (isMounted) {
            showError('Failed to send verification code. Please try again.');
          }
        }
      }
    };

    sendMfaCode();

    return () => {
      isMounted = false;
    };
  }, [isInitialized, isAuthenticated, passedMfa, showError]);

  const handleVerify = async (code, trustDevice) => {
    hideAlert();

    if (!code.trim()) {
      showError('Verification code is required.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await verifyMfa(code, trustDevice);

      if (response.success) {
        // Redirect to React search page
        navigate(ROUTES.SEARCH);
      } else {
        showError(response.error || 'Verification failed. Please try again.');
      }
    } catch (error) {
      console.error('MFA verification error:', error);
      showError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while checking auth state
  if (!isInitialized) {
    return (
      <div className="d-flex flex-column justify-content-center min-vh-100">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  return (
    <AuthPageLayout title="Verification Required">
      <MfaForm
        onSubmit={handleVerify}
        isSubmitting={isSubmitting}
        hiddenEmail={user?.hiddenEmail}
      />
    </AuthPageLayout>
  );
};

export default MfaPage;